import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "java",
  name: "Java",
  nameEn: "Java",
  seo: {
    title: "Javaを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Javaはエンタープライズ開発やAndroidアプリの定番言語です。ゆめさくエンジニアなら未経験からでもオブジェクト指向を基礎から学び、実践的なJavaスキルを習得できます。",
    url: "/columns/java-programming-school",
  },
  hero: {
    title: "Javaを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Java",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Java" },
  ],
  overview: {
    heading: "Javaとは？",
    paragraphs: [
      "Javaは1995年にSun Microsystemsによって開発されたオブジェクト指向プログラミング言語です。「Write Once, Run Anywhere（一度書けばどこでも動く）」を掲げ、OS に依存しない高い互換性を持っています。",
      "銀行の基幹システム、ECサイトのバックエンド、Androidアプリなど、大規模かつ信頼性が求められる開発現場で圧倒的なシェアを誇ります。世界中の企業で30年以上にわたって使い続けられている安定した言語です。",
      "日本国内でもJavaエンジニアの求人数はトップクラスであり、SIer・金融・官公庁・大手メーカーなど幅広い業界で需要があります。",
    ],
    useCases: [
      {
        label: "業務システム開発",
        description:
          "銀行の勘定系システムや保険の契約管理など、正確さと安定性が求められるエンタープライズシステムをSpring Bootなどで構築します。",
      },
      {
        label: "Androidアプリ開発",
        description:
          "KotlinとともにAndroidアプリの開発言語として使われています。既存のJava資産を活かしたアプリ保守・拡張案件も多数あります。",
      },
      {
        label: "WebアプリのAPI開発",
        description:
          "Spring FrameworkやJakarta EEを活用し、堅牢なREST APIやマイクロサービスを構築できます。",
      },
      {
        label: "バッチ処理・データパイプライン",
        description:
          "大量データの定期処理やETLパイプラインなど、高いパフォーマンスと信頼性が求められるバッチ処理に適しています。",
      },
    ],
  },
  whyLearn: {
    heading: "Javaを学ぶべき3つの理由",
    items: [
      {
        title: "求人数が国内最大級で就職に有利",
        description:
          "Java関連の求人は日本国内で常にトップクラスの数を維持しています。SIer、金融機関、官公庁など安定した職場への転職が実現しやすく、未経験からでもポテンシャル採用の門戸が広い言語です。",
      },
      {
        title: "オブジェクト指向の基礎が身につく",
        description:
          "Javaはオブジェクト指向を厳格に学べる言語です。クラス設計やデザインパターンなどの考え方は他の言語にも応用でき、エンジニアとしての基礎力を高めることができます。",
      },
      {
        title: "大規模開発の現場で長く使えるスキル",
        description:
          "Javaは後方互換性を大切にする文化があり、一度身につけたスキルが陳腐化しにくいのが特徴です。10年以上運用されるシステムも多く、安定したキャリアを築きたい方に最適です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでJavaを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 2のバックエンド基礎としてJavaを学びます。Phase 1で学んだWebの基礎知識を土台に、オブジェクト指向の考え方から実践的なAPI開発まで段階的にステップアップしていきます。",
      "Spring Bootを使った実践プロジェクトに取り組むことで、現場で即戦力となるスキルを養います。メンターがコードレビューを行うため、良い設計習慣を自然に身につけることができます。",
    ],
    topics: [
      "Java基礎文法・データ型・制御構文",
      "オブジェクト指向（クラス・継承・インターフェース）",
      "コレクションフレームワーク・Stream API",
      "Spring Boot入門・DI/AOP",
      "JPA/Hibernateによるデータベース連携",
      "REST API設計と実装（Spring MVC）",
    ],
  },
  careerPaths: {
    heading: "Javaを活かせるキャリア",
    items: [
      {
        title: "バックエンドエンジニア",
        description:
          "Spring BootやJakarta EEを使ったWebアプリケーションのサーバーサイド開発を担当します。大規模トラフィックに対応するAPIやマイクロサービスの設計・実装を行います。",
        salary: "450〜750万円",
      },
      {
        title: "SIer / 業務システムエンジニア",
        description:
          "銀行・保険・官公庁などの業務システムの設計・開発・保守を行います。要件定義から設計・テストまで上流工程に携わるキャリアパスも開けます。",
        salary: "400〜650万円",
      },
      {
        title: "Androidアプリエンジニア",
        description:
          "JavaやKotlinを使ったAndroidアプリの開発を担当します。既存Javaコードベースの保守・機能拡張案件で、Java経験者は重宝されています。",
        salary: "450〜700万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "8〜14ヶ月（独学で基礎習得）",
        luagate: "3〜5ヶ月（効率的なカリキュラム）",
      },
      {
        label: "挫折率",
        selfStudy: "オブジェクト指向で約80%が挫折",
        luagate: "メンターが設計の考え方から丁寧にサポート",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "教材のサンプルコード止まりになりがち",
        luagate: "Spring Bootを使った実務レベルのAPI開発を経験",
      },
      {
        label: "質問対応",
        selfStudy: "エラーメッセージの解読に何時間もかかる",
        luagate: "現役エンジニアのメンターに直接質問可能",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "JavaとJavaScriptは同じ言語ですか？",
      answer:
        "いいえ、まったく別の言語です。Javaはサーバーサイドやモバイルアプリ開発に使われるオブジェクト指向言語、JavaScriptはWebのフロントエンド開発が主な用途です。名前は似ていますが、文法も用途も異なります。",
    },
    {
      question: "プログラミング未経験ですが、Javaから始めても大丈夫ですか？",
      answer:
        "ゆめさくエンジニアではPhase 1でWeb基礎をしっかり学んだうえでJavaに入るため、未経験からでも段階的にスキルを積み上げられます。オブジェクト指向という考え方が最初は抽象的に感じるかもしれませんが、メンターが具体例を交えて丁寧に解説します。",
    },
    {
      question: "Javaは古い言語と聞きましたが、将来性はありますか？",
      answer:
        "Javaは半年ごとにバージョンアップを続けており、Record型やパターンマッチングなど最新の言語機能も取り入れています。エンタープライズ領域では今後も主力言語であり続けると予測されており、求人数も安定しています。",
    },
    {
      question: "JavaとKotlinのどちらを学ぶべきですか？",
      answer:
        "Androidアプリ開発が目的ならKotlin、エンタープライズやバックエンド開発ならJavaがおすすめです。KotlinはJVM上で動くためJavaの知識が土台として活きます。まずJavaで基礎を固め、その後Kotlinに進むのがスムーズです。",
    },
  ],
  relatedLanguages: [
    { slug: "sql", name: "SQL" },
    { slug: "rest-api", name: "REST API設計" },
    { slug: "system-design", name: "システム設計" },
    { slug: "python", name: "Python" },
  ],
};
